// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, EyeOff, Mail, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { FormBuilder } from "./FormBuilder";
import {
  COACH_DIVISIONS, DEFAULT_COACH_FIELDS,
  type CoachDivision, type EventCoach, type EventCoachPricing, type EventData,
  type FormFieldConfig,
} from "@/types/events";

type Tab = "pricing" | "form" | "coaches";

interface Props {
  event: EventData;
  updateEvent: (patch: Partial<EventData>) => void;
  eventId: string | undefined;
}

export function CoachAttendanceSection({ event, updateEvent, eventId }: Props) {
  const [tab, setTab] = useState<Tab>("pricing");
  const enabled = !!event.coach_attendance_enabled;
  const paid = event.coach_attendance_paid !== false;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold tracking-tight">COACH ATTENDANCE</h2>
        <p className="text-sm text-muted-foreground mt-1">
          College coaches registered for this event.
        </p>
      </div>

      {/* Top toggles */}
      <div className="space-y-3 p-4 rounded-lg border border-border bg-card">
        <ToggleRow
          checked={enabled}
          onChange={(v) => updateEvent({ coach_attendance_enabled: v })}
          label="Enable coach attendance for this event"
        />
        {enabled && (
          <>
            <ToggleRow
              checked={paid}
              onChange={(v) => updateEvent({ coach_attendance_paid: v })}
              label="Charge for coach attendance"
            />
            <ToggleRow
              checked={!!event.is_ncaa_live_event}
              onChange={(v) => updateEvent({ is_ncaa_live_event: v })}
              label="This is an NCAA Live event"
              caption="When enabled, coaches will be marked as NCAA Live attendees"
            />
          </>
        )}
      </div>

      {!enabled || !eventId ? (
        <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
          {!eventId
            ? "Save the event first to manage coach attendance."
            : "Enable coach attendance to configure pricing, the registration form, and registered coaches."}
        </div>
      ) : (
        <>
          {/* Sub-tabs */}
          <div className="flex border-b border-border">
            {(
              [
                ...(paid ? [{ id: "pricing", label: "Pricing" } as const] : []),
                { id: "form", label: "Form Builder" } as const,
              ]
            ).map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  "px-4 py-2 text-sm font-medium border-b-2 -mb-px",
                  tab === t.id
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "pricing" && paid && <PricingTab eventId={eventId} />}
          {(tab === "form" || (tab === "pricing" && !paid)) && (
            <FormBuilder
              eventId={eventId}
              formType="coach_attendance"
              defaultFields={DEFAULT_COACH_FIELDS}
              defaultTitle="College Coach Attendance Registration"
              defaultDescription="Register your college coaches for attendance at this event."
              defaultThankYou="Thanks — your coach attendance registration has been received."
            />
          )}
        </>
      )}
    </div>
  );
}

function ToggleRow({
  checked, onChange, label, caption,
}: { checked: boolean; onChange: (v: boolean) => void; label: string; caption?: string }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {caption && <span className="block text-xs text-muted-foreground">{caption}</span>}
      </span>
    </label>
  );
}

/* ───── Pricing Tab ───── */
function PricingTab({ eventId }: { eventId: string }) {
  const [rows, setRows] = useState<EventCoachPricing[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const ensureSeed = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("event_coach_pricing")
      .select("*")
      .eq("event_id", eventId)
      .order("display_order");
    let list = (data ?? []) as EventCoachPricing[];
    const missing = COACH_DIVISIONS.filter((d) => !list.find((r) => r.division === d));
    if (missing.length) {
      const seed = missing.map((d, i) => ({
        event_id: eventId,
        division: d,
        base_price: 0,
        hospitality_per_day_price: 0,
        additional_coach_price: 0,
        is_active: false,
        display_order: COACH_DIVISIONS.indexOf(d),
      }));
      const { data: ins } = await supabase.from("event_coach_pricing").insert(seed).select();
      list = [...list, ...((ins ?? []) as any)];
    }
    list.sort((a, b) => a.display_order - b.display_order);
    setRows(list);
    setLoading(false);
  };

  useEffect(() => { ensureSeed(); /* eslint-disable-next-line */ }, [eventId]);

  const update = (id: string, patch: Partial<EventCoachPricing>) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const saveAll = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("event_coach_pricing")
      .upsert(
        rows.map((r) => ({
          id: r.id,
          event_id: r.event_id,
          division: r.division,
          base_price: Number(r.base_price || 0),
          hospitality_per_day_price: Number(r.hospitality_per_day_price || 0),
          additional_coach_price: Number(r.additional_coach_price || 0),
          is_active: !!r.is_active,
          display_order: r.display_order,
        }))
      );
    setSaving(false);
    if (error) toast.error("Save failed: " + error.message);
    else toast.success("Pricing saved");
  };

  if (loading) {
    return <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Division</th>
              <th className="text-left px-3 py-2">Base Price ($)</th>
              <th className="text-left px-3 py-2">Hospitality / Day ($)</th>
              <th className="text-left px-3 py-2">Additional Coach ($)</th>
              <th className="text-center px-3 py-2">Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={cn("border-t border-border", !r.is_active && "opacity-60")}>
                <td className="px-3 py-2 font-medium">{r.division}</td>
                <td className="px-3 py-2">
                  <Input type="number" className="h-8 w-24" value={r.base_price ?? 0}
                    onChange={(e) => update(r.id, { base_price: parseFloat(e.target.value) || 0 })} />
                </td>
                <td className="px-3 py-2">
                  <Input type="number" className="h-8 w-24" value={r.hospitality_per_day_price ?? 0}
                    onChange={(e) => update(r.id, { hospitality_per_day_price: parseFloat(e.target.value) || 0 })} />
                </td>
                <td className="px-3 py-2">
                  <Input type="number" className="h-8 w-24" value={r.additional_coach_price ?? 0}
                    onChange={(e) => update(r.id, { additional_coach_price: parseFloat(e.target.value) || 0 })} />
                </td>
                <td className="px-3 py-2 text-center">
                  <Checkbox checked={r.is_active} onCheckedChange={(v) => update(r.id, { is_active: !!v })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <button
          onClick={saveAll}
          disabled={saving}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save pricing"}
        </button>
      </div>
    </div>
  );
}

/* ───── Coaches Tab ───── */
type CoachFilter = "all" | "paid" | "pending" | "comp" | "hidden";

export function CoachesTab({ eventId, isNcaaLive }: { eventId: string; isNcaaLive: boolean }) {
  const [coaches, setCoaches] = useState<EventCoach[]>([]);
  const [formFields, setFormFields] = useState<FormFieldConfig[]>(DEFAULT_COACH_FIELDS);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<CoachFilter>("all");
  const [divisionFilter, setDivisionFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<EventCoach> | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    const [{ data: cs }, { data: cfg }] = await Promise.all([
      supabase.from("event_coaches").select("*").eq("event_id", eventId).order("created_at", { ascending: false }),
      supabase.from("event_form_configs").select("fields").eq("event_id", eventId).eq("form_type", "coach_attendance").maybeSingle(),
    ]);
    setCoaches((cs ?? []) as EventCoach[]);
    if (cfg && (cfg as any).fields) setFormFields((cfg as any).fields as FormFieldConfig[]);
    setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [eventId]);

  const filtered = useMemo(() => {
    return coaches.filter((c) => {
      if (filter === "paid" && c.payment_status !== "paid") return false;
      if (filter === "pending" && c.payment_status !== "pending" && c.payment_status !== "unpaid") return false;
      if (filter === "comp" && c.payment_status !== "comp") return false;
      if (filter === "hidden" && !c.hide_from_public) return false;
      if (divisionFilter !== "all" && c.division !== divisionFilter) return false;
      const q = search.trim().toLowerCase();
      if (q && !((c.coach_name || "").toLowerCase().includes(q) || (c.college_name || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [coaches, filter, divisionFilter, search]);

  const stats = useMemo(() => {
    const total = coaches.length;
    const paid = coaches.filter((c) => c.payment_status === "paid").length;
    const ncaaLive = coaches.filter((c) => c.is_ncaa_live).length;
    const revenue = coaches.reduce((s, c) => s + (c.amount_paid_cents || 0), 0) / 100;
    const byDiv: Record<string, number> = {};
    COACH_DIVISIONS.forEach((d) => (byDiv[d] = coaches.filter((c) => c.division === d).length));
    return { total, paid, ncaaLive, revenue, byDiv };
  }, [coaches]);

  const handleSave = async (draft: Partial<EventCoach>) => {
    if (!draft.coach_name?.trim()) {
      toast.error("Coach name is required");
      return;
    }
    const payload: any = {
      event_id: eventId,
      coach_name: draft.coach_name.trim(),
      coach_email: draft.coach_email || null,
      coach_phone: draft.coach_phone || null,
      college_name: draft.college_name || null,
      division: draft.division || null,
      hide_from_public: !!draft.hide_from_public,
      payment_status: draft.payment_status || "comp",
      amount_paid_cents: draft.amount_paid_cents || 0,
      filler_name: draft.filler_name || null,
      filler_email: draft.filler_email || null,
      form_data: draft.form_data || {},
      is_ncaa_live: draft.is_ncaa_live ?? isNcaaLive,
      notes: draft.notes || null,
    };
    let res;
    if (draft.id) res = await supabase.from("event_coaches").update(payload).eq("id", draft.id);
    else res = await supabase.from("event_coaches").insert(payload);
    if (res.error) { toast.error("Save failed: " + res.error.message); return; }
    toast.success(draft.id ? "Coach updated" : "Coach added");
    setEditing(null);
    reload();
  };

  const markComp = async (c: EventCoach) => {
    const { error } = await supabase.from("event_coaches").update({ payment_status: "comp" }).eq("id", c.id);
    if (error) toast.error(error.message); else { toast.success("Marked comp"); reload(); }
  };
  const toggleHide = async (c: EventCoach) => {
    const { error } = await supabase.from("event_coaches").update({ hide_from_public: !c.hide_from_public }).eq("id", c.id);
    if (error) toast.error(error.message); else reload();
  };
  const del = async (id: string) => {
    const { error } = await supabase.from("event_coaches").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Deleted"); setDeletingId(null); reload(); }
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total" value={stats.total} />
        <Stat label="Paid" value={stats.paid} />
        <Stat label="NCAA Live" value={stats.ncaaLive} />
        <Stat label="Total Revenue" value={`$${stats.revenue.toFixed(0)}`} />
      </div>
      <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
        {COACH_DIVISIONS.map((d) => (
          <span key={d}><strong className="text-foreground">{d}:</strong> {stats.byDiv[d] || 0}</span>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {(["all","paid","pending","comp","hidden"] as CoachFilter[]).map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={cn(
              "px-3 py-1 rounded-full text-xs font-medium border",
              filter === f ? "border-primary bg-primary/10 text-foreground" : "border-input text-muted-foreground"
            )}>
            {f === "all" ? "All" : f[0].toUpperCase() + f.slice(1)}
          </button>
        ))}
        <select value={divisionFilter} onChange={(e) => setDivisionFilter(e.target.value)}
          className="h-8 rounded-md border border-input bg-background px-2 text-xs">
          <option value="all">All divisions</option>
          {COACH_DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2 top-2 w-4 h-4 text-muted-foreground" />
          <Input className="pl-8 h-8" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name/college…" />
        </div>
        <button
          onClick={() => setEditing({ payment_status: "comp", form_data: {}, is_ncaa_live: isNcaaLive })}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm"
        >
          <Plus className="w-4 h-4" /> Add Coach Manually
        </button>
        <button
          onClick={() => toast.info("Bulk CSV import coming soon")}
          className="px-3 py-1.5 rounded-md border border-input text-sm"
        >
          Bulk Import (CSV)
        </button>
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No coaches yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Coach</th>
                  <th className="text-left px-3 py-2">College</th>
                  <th className="text-left px-3 py-2">Division</th>
                  <th className="text-left px-3 py-2">Filler</th>
                  <th className="text-left px-3 py-2">Hospitality</th>
                  <th className="text-left px-3 py-2">Payment</th>
                  <th className="text-center px-3 py-2">NCAA Live</th>
                  <th className="text-center px-3 py-2">Hide</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const hospitality = (c.form_data?.hospitality_days || []) as string[] | string;
                  const hospText = Array.isArray(hospitality) ? hospitality.join(", ") : (hospitality || "—");
                  return (
                    <tr key={c.id} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{c.coach_name}</td>
                      <td className="px-3 py-2">{c.college_name || "—"}</td>
                      <td className="px-3 py-2">{c.division || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        {c.filler_name || "—"}
                        {c.filler_email && <div className="text-muted-foreground">{c.filler_email}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs">{hospText || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={cn(
                          "inline-flex px-2 py-0.5 rounded-full text-xs",
                          c.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" :
                          c.payment_status === "comp" ? "bg-blue-100 text-blue-800" :
                          c.payment_status === "pending" ? "bg-amber-100 text-amber-800" :
                          "bg-gray-100 text-gray-700"
                        )}>{c.payment_status}</span>
                      </td>
                      <td className="px-3 py-2 text-center">{c.is_ncaa_live ? "✓" : ""}</td>
                      <td className="px-3 py-2 text-center">{c.hide_from_public ? "✓" : ""}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <IconBtn title="Edit" onClick={() => setEditing(c)}><Pencil className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Mark Comp" onClick={() => markComp(c)}>$</IconBtn>
                          <IconBtn title="Hide from public" onClick={() => toggleHide(c)}><EyeOff className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Resend confirmation" onClick={() => toast.info("TODO: resend confirmation email")}><Mail className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Delete" onClick={() => setDeletingId(c.id)}><Trash2 className="w-4 h-4 text-red-600" /></IconBtn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <CoachEditDialog
          draft={editing}
          formFields={formFields}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      <Dialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete coach?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This cannot be undone.</p>
          <DialogFooter>
            <button className="px-3 py-2 rounded-md border border-input text-sm" onClick={() => setDeletingId(null)}>Cancel</button>
            <button className="px-3 py-2 rounded-md bg-red-600 text-white text-sm" onClick={() => deletingId && del(deletingId)}>Delete</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="p-3 rounded-lg border border-border bg-card">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function IconBtn({ children, onClick, title }: { children: React.ReactNode; onClick: () => void; title: string }) {
  return (
    <button type="button" title={title} onClick={onClick}
      className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground inline-flex items-center justify-center min-w-[28px] text-xs">
      {children}
    </button>
  );
}

/* ───── Coach Edit Dialog (renders per FormFieldConfig) ───── */
function CoachEditDialog({
  draft: initial, formFields, onClose, onSave,
}: {
  draft: Partial<EventCoach>;
  formFields: FormFieldConfig[];
  onClose: () => void;
  onSave: (d: Partial<EventCoach>) => Promise<void>;
}) {
  const [draft, setDraft] = useState<Partial<EventCoach>>(initial);
  const [saving, setSaving] = useState(false);

  // top-level fields that map directly to columns
  const COLUMN_KEYS = new Set(["coach_name", "coach_email", "coach_phone", "college_name", "division", "filler_name", "filler_email"]);

  const getValue = (f: FormFieldConfig) => {
    if (COLUMN_KEYS.has(f.key)) return (draft as any)[f.key] ?? "";
    return draft.form_data?.[f.key] ?? (f.type === "multi_select" || f.type === "repeater" ? [] : "");
  };
  const setValue = (f: FormFieldConfig, v: any) => {
    if (COLUMN_KEYS.has(f.key)) {
      setDraft((d) => ({ ...d, [f.key]: v }));
    } else {
      setDraft((d) => ({ ...d, form_data: { ...(d.form_data || {}), [f.key]: v } }));
    }
  };

  const submit = async () => {
    setSaving(true);
    await onSave(draft);
    setSaving(false);
  };

  const sorted = [...formFields].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{draft.id ? "Edit coach" : "Add coach manually"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {sorted.map((f) => (
            <FormFieldRender key={f.key} field={f} value={getValue(f)} onChange={(v) => setValue(f, v)} />
          ))}

          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
            <div>
              <Label>Payment status</Label>
              <select
                value={draft.payment_status || "comp"}
                onChange={(e) => setDraft({ ...draft, payment_status: e.target.value as any })}
                className="w-full h-10 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="comp">Comp</option>
                <option value="unpaid">Unpaid</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="waived">Waived</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
            <div>
              <Label>Amount paid (cents)</Label>
              <Input type="number" value={draft.amount_paid_cents ?? 0}
                onChange={(e) => setDraft({ ...draft, amount_paid_cents: parseInt(e.target.value) || 0 })} />
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <Checkbox checked={!!draft.is_ncaa_live} onCheckedChange={(v) => setDraft({ ...draft, is_ncaa_live: !!v })} />
              NCAA Live attendance certified
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <Checkbox checked={!!draft.hide_from_public} onCheckedChange={(v) => setDraft({ ...draft, hide_from_public: !!v })} />
              Hide from public
            </label>
          </div>
          <div>
            <Label>Notes</Label>
            <textarea rows={2} value={draft.notes || ""}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <button className="px-3 py-2 rounded-md border border-input text-sm" onClick={onClose}>Cancel</button>
          <button disabled={saving} onClick={submit}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FormFieldRender({
  field, value, onChange,
}: { field: FormFieldConfig; value: any; onChange: (v: any) => void }) {
  const lbl = (
    <Label>
      {field.label}{field.required && <span className="text-red-500"> *</span>}
    </Label>
  );
  const helper = field.helper_text ? <p className="text-xs text-muted-foreground mt-1">{field.helper_text}</p> : null;

  switch (field.type) {
    case "textarea":
      return (
        <div>{lbl}<textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />{helper}</div>
      );
    case "select":
      return (
        <div>{lbl}
          <select value={value || ""} onChange={(e) => onChange(e.target.value)}
            className="w-full h-10 rounded-md border border-input bg-background px-2 text-sm">
            <option value="">Select…</option>
            {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>{helper}
        </div>
      );
    case "multi_select":
      return (
        <div>{lbl}
          <div className="flex flex-wrap gap-2 mt-1">
            {(field.options || []).map((o) => {
              const arr = Array.isArray(value) ? value : [];
              const selected = arr.includes(o);
              return (
                <button type="button" key={o}
                  onClick={() => onChange(selected ? arr.filter((x: string) => x !== o) : [...arr, o])}
                  className={cn(
                    "px-3 py-1 rounded-full text-xs border",
                    selected ? "border-primary bg-primary/10" : "border-input"
                  )}>
                  {o}
                </button>
              );
            })}
          </div>{helper}
        </div>
      );
    case "checkbox":
      return (
        <label className="inline-flex items-center gap-2 text-sm">
          <Checkbox checked={!!value} onCheckedChange={(v) => onChange(!!v)} />
          {field.label}
        </label>
      );
    case "repeater": {
      const arr = Array.isArray(value) ? value : [];
      return (
        <div>{lbl}
          <div className="space-y-2">
            {arr.map((item: any, i: number) => (
              <div key={i} className="flex gap-2">
                <Input value={item?.name || ""}
                  onChange={(e) => {
                    const next = [...arr];
                    next[i] = { ...next[i], name: e.target.value };
                    onChange(next);
                  }}
                  placeholder="Name" />
                <button type="button" className="px-2 text-red-600"
                  onClick={() => onChange(arr.filter((_: any, j: number) => j !== i))}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => onChange([...arr, { name: "" }])}
              className="text-xs px-2 py-1 rounded border border-input">+ Add</button>
          </div>{helper}
        </div>
      );
    }
    case "number":
      return (
        <div>{lbl}<Input type="number" value={value ?? ""} placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))} />{helper}</div>
      );
    case "email":
      return <div>{lbl}<Input type="email" value={value || ""} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} />{helper}</div>;
    case "phone":
      return <div>{lbl}<Input value={value || ""} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} />{helper}</div>;
    default:
      return <div>{lbl}<Input value={value || ""} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} />{helper}</div>;
  }
}
